/**
 * Live stream view for events: feed with visibility filters.
 * Native mobile app style with modern slider.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconCalendar,
  IconSearch,
  IconCheck,
} from "../../../components/Icons.tsx";
import { haptic } from "../../../lib/telegram.ts";
import { useDebouncedValue } from "../../../lib/use-debounce.ts";
import { formatDateBangkok } from "../../../lib/bangkok-time.ts";
import {
  type AdminEventListItem,
  EVENT_STATUS_CONFIG,
  EVENT_VISIBILITY_CONFIG,
} from "./types.ts";
import {
  EventVisibilitySlider,
  type EventVisibilityFilter,
} from "./EventVisibilitySlider.tsx";

interface EventsLiveStreamProps {
  events: AdminEventListItem[];
  onRefresh: () => void;
}

export function EventsLiveStream({ events }: EventsLiveStreamProps) {
  const navigate = useNavigate();
  const [visibilityFilter, setVisibilityFilter] =
    useState<EventVisibilityFilter>("all");
  const [searchInput, setSearchInput] = useState("");

  const searchQuery = useDebouncedValue(searchInput, 200);

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      // Visibility/status filter
      if (visibilityFilter === "archived") {
        // Show archived and cancelled events
        if (event.status !== "ARCHIVED" && event.status !== "CANCELLED") {
          return false;
        }
      } else if (visibilityFilter !== "all") {
        // Filter by visibility type
        if (event.visibility !== visibilityFilter) {
          return false;
        }
        // Exclude archived/cancelled from visibility filters
        if (event.status === "ARCHIVED" || event.status === "CANCELLED") {
          return false;
        }
      }
      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        if (
          !event.title.toLowerCase().includes(search) &&
          !event.venue?.toLowerCase().includes(search) &&
          !event.subtitle?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [events, visibilityFilter, searchQuery]);

  // Sort: upcoming first, then by status
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const statusOrder = {
        PUBLISHED: 0,
        SCHEDULED: 1,
        DRAFT: 2,
        CANCELLED: 3,
        ARCHIVED: 4,
      };
      const statusDiff = statusOrder[a.status] - statusOrder[b.status];
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime();
    });
  }, [filteredEvents]);

  // Calculate counts for visibility filter
  const visibilityCounts = useMemo(() => {
    const counts: Partial<Record<EventVisibilityFilter, number>> = {
      all: events.length,
      PUBLIC: 0,
      CHURCH: 0,
      MINISTERS: 0,
      RESTRICTED: 0,
      archived: 0,
    };

    for (const event of events) {
      // Count archived separately
      if (event.status === "ARCHIVED" || event.status === "CANCELLED") {
        counts.archived = (counts.archived ?? 0) + 1;
      } else {
        // Count by visibility (excluding archived)
        counts[event.visibility] = (counts[event.visibility] ?? 0) + 1;
      }
    }

    return counts;
  }, [events]);

  const hasActiveFilters = !!(visibilityFilter !== "all" || searchInput.trim());

  return (
    <div>
      {/* Visibility filter */}
      <EventVisibilitySlider
        value={visibilityFilter}
        onChange={setVisibilityFilter}
        counts={visibilityCounts}
        className="mb-4"
      />

      {/* Search */}
      <div className="relative mb-3">
        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск мероприятий..."
          className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-slate-100 border-0 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/20"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-300 text-slate-600 flex items-center justify-center text-sm leading-none active:opacity-80"
          >
            ×
          </button>
        )}
      </div>

      {/* Results info */}
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[11px] text-slate-400">
          {filteredEvents.length === 0
            ? "Нет мероприятий"
            : events.length > filteredEvents.length
              ? `Показано ${filteredEvents.length} из ${events.length}`
              : `Всего: ${filteredEvents.length}`}
        </span>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setVisibilityFilter("all");
              setSearchInput("");
            }}
            className="text-[12px] font-medium text-[var(--color-accent)] active:opacity-80"
          >
            Сбросить
          </button>
        )}
      </div>

      {/* Events list */}
      {sortedEvents.length === 0 ? (
        <div className="py-12 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-slate-100 flex items-center justify-center">
            <IconCalendar className="w-6 h-6 text-slate-400" />
          </div>
          <p className="text-[14px] text-slate-500">Мероприятия не найдены</p>
          {hasActiveFilters && (
            <p className="text-[12px] text-slate-400 mt-1">
              Попробуйте изменить фильтры
            </p>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          {sortedEvents.map((event, idx) => {
            const statusConfig = EVENT_STATUS_CONFIG[event.status];
            const visibilityConfig = EVENT_VISIBILITY_CONFIG[event.visibility];

            return (
              <button
                key={event.id}
                type="button"
                onClick={() => {
                  haptic("light");
                  navigate(`/admin/events/${event.id}`);
                }}
                className={`w-full flex items-center gap-3 min-h-[72px] px-4 py-3 active:bg-slate-50 transition-colors text-left ${
                  idx > 0 ? "border-t border-slate-100" : ""
                }`}
              >
                {/* Image or placeholder */}
                {event.imageUrl ? (
                  <img
                    src={event.imageUrl}
                    alt=""
                    className="w-14 h-14 object-cover rounded-xl flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <IconCalendar className="w-6 h-6 text-slate-300" />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-slate-900 text-[15px] truncate">
                      {event.title}
                    </span>
                    {event.featured && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold text-amber-600 bg-amber-100 rounded">
                        ★
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-slate-500 mb-1">
                    {formatDateBangkok(event.startsAt)}
                    {event.venue && (
                      <span className="text-slate-400"> · {event.venue}</span>
                    )}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded ${statusConfig.bgColor} ${statusConfig.color}`}
                    >
                      {event.status === "PUBLISHED" && (
                        <IconCheck className="w-2.5 h-2.5" />
                      )}
                      {statusConfig.label}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${visibilityConfig.bgColor} ${visibilityConfig.color}`}
                    >
                      {visibilityConfig.label}
                    </span>
                    {event.categories[0] && (
                      <span
                        className="px-1.5 py-0.5 text-[10px] font-medium rounded"
                        style={{
                          backgroundColor: event.categories[0].color
                            ? `${event.categories[0].color}20`
                            : "#f1f5f9",
                          color: event.categories[0].color || "#64748b",
                        }}
                      >
                        {event.categories[0].name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Chevron */}
                <svg
                  className="w-5 h-5 text-slate-300 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
